<template>
  <menu-group class="itemheader">
    <div class="itemheader__contenttype">
      <span>
        {{ label || $t(`objectNames.${type}`) }}
      </span>
      <inline-popover
        v-if="choosePopover.visible"
        :left="choosePopover.left"
        :top="choosePopover.top"
        :items="itemsArray"
        marker=""
        @select="onSelect"
        @close="closeItemPopover"
      >
        <template #default="{ item }">
          <span v-if="item?.title">
            {{ item.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
      </inline-popover>
    </div>
    <div class="itemheader__content">
      <slot name="content">
        <input-dropdown
          v-if="choosable && withDropdown"
          class="itemheader__dropdown mt-1"
          :options="items"
          :order="itemOrder"
          :prop="prop"
          :select="selectedKey ?? undefined"
          :searchable="true"
          @select="onSelect"
        />
        <h2 v-else class="itemheader__title">
          <slot v-if="hasSlotContent($slots.default)"></slot>
          <div v-else class="itemheader__title-placeholder">{{ $t("labels.unnamed") }}</div>
        </h2>
      </slot>
    </div>
    <button
      v-if="closable && (!choosable || selectedId != null)"
      class="button-icon itemheader__closebutton"
      :class="{ '-center': withDropdown || buttonCentered }"
      type="button"
      @click="onDeselect"
    >
      <lucide-icon name="x" />
    </button>
    <button v-if="choosable && !withDropdown" class="itemheader__searchbutton button-icon" @click="openChoosePopover($event)">
      <lucide-icon name="search" />
    </button>
  </menu-group>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";

import { useSlotsContent } from "@core/composables/useSlotsContent";
import { useModelStore } from "@editor/items/model/model.store";

import InputDropdown from "@core/input/InputDropdown.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import InlinePopover from "@core/input/InlinePopover.vue";

import type { ItemType } from "@editor/items/ItemOfStore";
import { applyOrder } from "@/helpers/utility/Array";

const props = withDefaults(defineProps<{
  type?: ItemType;
  selectedId?: number | null;
  nullOption?: string;
  label?: string;
  closable?: boolean;
  objects?: Record<string, { title: string; id: number }>;
  prop?: string;
  itemOrder?: string[];
  choosable?: boolean;
  withDropdown?: boolean;
  buttonCentered?: boolean;
}>(), {
  selectedId: undefined,
  nullOption: undefined,
  label: undefined,
  closable: true,
  objects: undefined,
  prop: "title",
  itemOrder: undefined,
  choosable: false,
  withDropdown: false,
  buttonCentered: false
});

const emit = defineEmits<{
  (e: "select", id: number | null): void;
  (e: "close"): void;
}>();

const { hasSlotContent } = useSlotsContent();

const storeModule = computed(() => {
  if (!props.type) return null;
  return useModelStore().storesByType[props.type];
});

const items = computed(() => {
  let result: Record<string, { title?: string | null; id?: number }>;

  // Either use the passed objects or the store items
  if (props.objects) result = { ...props.objects };
  else if (storeModule.value) {
    result = { ...storeModule.value.items };
  } else {
    result = {};
  }

  if (props.nullOption) {
    result.null = { title: props.nullOption };
  }

  return result;
});
const itemsArray = computed(() => {
  if (!storeModule.value) return [];
  return applyOrder(items.value, props.itemOrder || storeModule.value.order).map((item) => ({
    title: item.title || "",
    id: item.id ?? null,
    key: item.id ?? null
  }));
});

const selectedKey = computed(() => {
  if (props.nullOption && props.selectedId == null) return "null";
  return props.selectedId;
});

function onSelect(option: { key: string }) {
  if (option.key === "null") {
    onDeselect();
  } else {
    emit("select", parseInt(option.key));
  }
}
function onDeselect() {
  emit("close");
  emit("select", null);
}

// ===================
// Choose Popover

const choosePopover = reactive({
  visible: false,
  top: 0,
  left: 0
});

function openChoosePopover(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const rect = target.getBoundingClientRect();
  const left = rect.left - 5;
  const top = rect.top;

  choosePopover.top = top;
  choosePopover.left = left;
  choosePopover.visible = true;
}

function closeItemPopover() {
  choosePopover.visible = false;
}
</script>

<style lang="scss">
.itemheader {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  row-gap: 0.125rem;
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
  align-items: flex-start;
  grid-auto-columns: max-content;
  flex-shrink: 0;
  background-color: var(--color-background-shallow);

  &__closebutton {
    font-size: 18px;
    padding: 0;
    grid-row-start: 1;
    grid-column-start: 2;
    grid-column-end: 3;

    &.-center {
      grid-row-start: 2;
      align-self: center;
    }
  }

  &__title {
    font-size: 16px;
    margin: 0;
    color: var(--color-text);
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__title-placeholder {
    font-style: italic;
  }

  &__contenttype {
    font-size: 12px;
    color: var(--color-text-medium);
    border-radius: 4px;
    grid-column-start: 1;
    grid-column-end: 2;
    grid-row-start: 1;
    grid-row-end: 2;
    align-self: end;
  }

  &__content {
    grid-column-start: 1;
    grid-column-end: 2;
    grid-row-start: 2;
    grid-row-end: 3;
  }

  &__dropdown {
    margin-left: -2px;
  }

  &__searchbutton {
    grid-row: 2 / 2;
    grid-column: 2 / 3;
    padding: 0;
  }
}
</style>
